/**
 * Modal pour générer / lister / révoquer les liens de partage d'une analyse.
 */

import { useState, useEffect, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ShareInfo {
  id: number;
  token: string;
  createdAt: string;
  expiresAt: string | null;
  viewsCount: number;
}

interface Props {
  analyseId: number;
  onClose: () => void;
}

const DURATION_OPTIONS = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '3 mois' },
  { value: 0, label: 'Sans expiration' },
];

export function ShareAnalyseModal({ analyseId, onClose }: Props) {
  const [shares, setShares] = useState<ShareInfo[]>([]);
  const [duration, setDuration] = useState<number>(30);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [error, setError] = useState('');

  const headers = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const loadShares = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/shares?analyseId=${analyseId}`, { headers: headers() });
      const data = await res.json();
      if (data.success) setShares(data.data);
    } catch {
      setError('Impossible de charger les liens existants');
    } finally {
      setLoading(false);
    }
  }, [analyseId, headers]);

  useEffect(() => { loadShares(); }, [loadShares]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const body: { analyseId: number; durationDays?: number } = { analyseId };
      if (duration > 0) body.durationDays = duration;
      const res = await fetch(`${API_BASE}/shares`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setShares((prev) => [data.data, ...prev]);
      } else {
        setError(data.error ?? 'Échec de la création');
      }
    } catch {
      setError('Erreur réseau');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!confirm('Révoquer ce lien ? Il deviendra inaccessible.')) return;
    try {
      const res = await fetch(`${API_BASE}/shares/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (res.ok) setShares((prev) => prev.filter((s) => s.id !== id));
    } catch { /* silencieux */ }
  };

  const buildUrl = (token: string): string => {
    return `${window.location.origin}/p/${token}`;
  };

  const handleCopy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(buildUrl(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      setError('Impossible de copier — sélectionne le lien manuellement');
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, maxWidth: 640, width: '100%',
          maxHeight: '90vh', overflow: 'auto', padding: 24,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1a1a1a' }}>
            <i className="bi bi-share" style={{ marginRight: 8, color: '#c9a227' }} />
            Partager cette analyse
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 0, fontSize: 22, cursor: 'pointer', color: '#666' }}
            aria-label="Fermer"
          >×</button>
        </div>

        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 18 }}>
          Génère un lien public que tu peux envoyer à ton client. Le lien fonctionne sans login,
          peut être révoqué à tout moment, et expire selon la durée choisie.
        </p>

        {/* Création d'un lien */}
        <div style={{
          background: '#f8f6f0', border: '1px solid #f5e6b3', borderRadius: 8,
          padding: 16, marginBottom: 18,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#3D3830' }}>
              Durée de validité :
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value, 10))}
              style={{
                padding: '6px 10px', borderRadius: 6, border: '1px solid #c9a227',
                background: '#fff', fontSize: '0.85rem', fontFamily: 'inherit',
              }}
            >
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={handleCreate}
              disabled={creating}
              style={{
                padding: '6px 16px', borderRadius: 6, border: 0,
                background: creating ? '#e0b83a' : '#c9a227',
                color: '#1a1a1a', fontSize: '0.85rem', fontWeight: 600,
                cursor: creating ? 'wait' : 'pointer', fontFamily: 'inherit',
                marginLeft: 'auto',
              }}
            >
              <i className={creating ? 'bi bi-hourglass-split' : 'bi bi-plus-lg'} style={{ marginRight: 4 }} />
              {creating ? 'Création…' : 'Générer un lien'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
            padding: 10, fontSize: '0.85rem', color: '#991b1b', marginBottom: 14,
          }}>
            {error}
          </div>
        )}

        {/* Liste des liens existants */}
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a' }}>
            Liens actifs ({shares.length})
          </h4>
          {loading && (
            <p style={{ fontSize: '0.85rem', color: '#666' }}>Chargement…</p>
          )}
          {!loading && shares.length === 0 && (
            <p style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
              Aucun lien actif. Génères-en un ci-dessus.
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shares.map((share) => {
              const url = buildUrl(share.token);
              const expired = share.expiresAt && new Date(share.expiresAt).getTime() < Date.now();
              return (
                <div key={share.id} style={{
                  border: '1px solid #e0e0e0', borderRadius: 8, padding: 12,
                  background: expired ? '#fafafa' : '#fff',
                  opacity: expired ? 0.6 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <input
                      type="text"
                      readOnly
                      value={url}
                      onFocus={(e) => e.currentTarget.select()}
                      style={{
                        flex: 1, padding: '6px 10px', borderRadius: 5,
                        border: '1px solid #e0e0e0', fontSize: '0.8rem',
                        fontFamily: 'JetBrains Mono, monospace', background: '#fafafa',
                      }}
                    />
                    <button
                      onClick={() => handleCopy(share.token)}
                      style={{
                        padding: '6px 12px', borderRadius: 5, border: '1px solid #c9a227',
                        background: copiedToken === share.token ? '#c9a227' : '#fff',
                        color: copiedToken === share.token ? '#1a1a1a' : '#8a6e16',
                        fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit',
                        whiteSpace: 'nowrap', fontWeight: 600,
                      }}
                    >
                      <i className={copiedToken === share.token ? 'bi bi-check-lg' : 'bi bi-clipboard'} style={{ marginRight: 4 }} />
                      {copiedToken === share.token ? 'Copié' : 'Copier'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: '#666' }}>
                    <span>
                      {expired ? '⚠ Expiré · ' : ''}
                      {share.expiresAt
                        ? `Expire le ${new Date(share.expiresAt).toLocaleDateString('fr-FR')}`
                        : 'Sans expiration'}
                      {' · '}{share.viewsCount} vue{share.viewsCount > 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={() => handleRevoke(share.id)}
                      style={{
                        background: 'transparent', border: 0, color: '#dc2626',
                        cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit',
                        padding: 0, textDecoration: 'underline',
                      }}
                    >
                      Révoquer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
